import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, MessageCircle, Filter, Star as StarFilled, X, Send } from 'lucide-react';
import { reviewService } from '../services/reviewService';
import { replyService } from '../services/replyService';
import './Reviews.css';

const Reviews = () => {
  const [selectedRating, setSelectedRating] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [reviews, setReviews] = useState([]);
  const [replies, setReplies] = useState({});
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [votedReviews, setVotedReviews] = useState(new Set());

  // Review form state
  const [reviewForm, setReviewForm] = useState({
    user_name: '',
    rating: 5,
    title: '',
    content: ''
  });

  // Load reviews and replies from database
  useEffect(() => {
    loadReviews();
    loadAllReplies();
    loadVotedReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const reviewsData = await reviewService.getReviews();
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllReplies = async () => {
    try {
      const repliesData = await replyService.getAllReplies();
      
      // Group replies by review_id
      const repliesByReview = {};
      repliesData.forEach(reply => {
        if (!repliesByReview[reply.review_id]) {
          repliesByReview[reply.review_id] = [];
        }
        repliesByReview[reply.review_id].push(reply);
      });
      
      setReplies(repliesByReview);
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  };

  const loadVotedReviews = () => {
    // Load voted reviews from localStorage
    const voted = localStorage.getItem('votedReviews');
    if (voted) {
      setVotedReviews(new Set(JSON.parse(voted)));
    }
  };

  const saveVotedReviews = (votedSet) => {
    // Save voted reviews to localStorage
    localStorage.setItem('votedReviews', JSON.stringify([...votedSet]));
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.user_name || !reviewForm.title || !reviewForm.content) {
      setSubmitStatus('error');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitStatus(null);

      const reviewData = {
        ...reviewForm,
        helpful: 0,
        verified: false,
        published: true
      };

      await reviewService.createReview(reviewData);
      
      // Reset form
      setReviewForm({
        user_name: '',
        rating: 5,
        title: '',
        content: ''
      });
      
      setSubmitStatus('success');
      setShowReviewForm(false);
      
      // Reload reviews to show the new one
      await loadReviews();
      
    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingClick = (rating) => {
    setReviewForm(prev => ({
      ...prev,
      rating: rating
    }));
  };

  const ratingFilters = [
    { value: 'all', label: 'All Ratings' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4 Stars' },
    { value: '3', label: '3 Stars' },
    { value: '2', label: '2 Stars' },
    { value: '1', label: '1 Star' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'highest', label: 'Highest Rated' },
    { value: 'lowest', label: 'Lowest Rated' },
    { value: 'most_helpful', label: 'Most Helpful' }
  ];

  const filteredReviews = reviews.filter(review => {
    if (selectedRating === 'all') return true;
    return review.rating === parseInt(selectedRating);
  });

  const sortedReviews = [...filteredReviews].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'highest':
        return b.rating - a.rating;
      case 'lowest':
        return a.rating - b.rating;
      case 'most_helpful':
        return b.helpful - a.helpful;
      default:
        return 0;
    }
  });

  const averageRating = reviews.length > 0 ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length : 0;
  const totalReviews = reviews.length;
  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: reviews.filter(review => review.rating === rating).length,
    percentage: reviews.length > 0 ? (reviews.filter(review => review.rating === rating).length / totalReviews) * 100 : 0
  }));

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <StarFilled
        key={index}
        className={`star ${index < rating ? 'filled' : 'empty'}`}
        size={16}
      />
    ));
  };

  const handleHelpfulClick = async (reviewId, currentHelpful) => {
    // Check if user has already voted on this review
    if (votedReviews.has(reviewId)) {
      return; // Already voted, do nothing
    }

    try {
      const newHelpfulCount = currentHelpful + 1;
      await reviewService.updateHelpfulCount(reviewId, newHelpfulCount);
      
      // Update local state
      setReviews(prevReviews => 
        prevReviews.map(review => 
          review.id === reviewId 
            ? { ...review, helpful: newHelpfulCount }
            : review
        )
      );

      // Mark this review as voted
      const newVotedReviews = new Set([...votedReviews, reviewId]);
      setVotedReviews(newVotedReviews);
      saveVotedReviews(newVotedReviews);
      
    } catch (error) {
      console.error('Error updating helpful count:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="reviews">
        <section className="reviews-hero">
          <div className="container">
            <h1>Customer Reviews</h1>
            <p>Loading reviews...</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="reviews">
      {/* Hero Section */}
      <section className="reviews-hero">
        <div className="container">
          <h1>Customer Reviews</h1>
          <p>
            See what our users say about Kenny Miles Exchange. 
            Real feedback from real traders.
          </p>
        </div>
      </section>

      {/* Overall Rating Section */}
      {reviews.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="overall-rating">
              <div className="rating-summary">
                <div className="rating-number">{averageRating.toFixed(1)}</div>
                <div className="rating-stars">
                  {renderStars(Math.round(averageRating))}
                </div>
                <div className="rating-text">out of 5</div>
                <div className="total-reviews">{totalReviews} reviews</div>
              </div>
              
              <div className="rating-breakdown">
                {ratingDistribution.map(({ rating, count, percentage }) => (
                  <div key={rating} className="rating-bar">
                    <span className="rating-label">{rating} stars</span>
                    <div className="rating-progress">
                      <div 
                        className="rating-fill" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="rating-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filters and Sorting */}
      <section className="section">
        <div className="container">
          <div className="filters-container">
            <div className="rating-filters">
              <span className="filter-label">Filter by rating:</span>
              <div className="filter-buttons">
                {ratingFilters.map(filter => (
                  <button
                    key={filter.value}
                    className={`filter-btn ${selectedRating === filter.value ? 'active' : ''}`}
                    onClick={() => setSelectedRating(filter.value)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="sort-container">
              <span className="filter-label">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews List */}
      <section className="section">
        <div className="container">
          <div className="reviews-list">
            {sortedReviews.length > 0 ? (
              sortedReviews.map(review => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <div className="review-user">
                      <div className="user-avatar">
                        {review.user_name.charAt(0)}
                      </div>
                      <div className="user-info">
                        <div className="user-name">
                          {review.user_name}
                          {review.verified && <span className="verified-badge">✓ Verified</span>}
                        </div>
                        <div className="review-date">{formatDate(review.created_at)}</div>
                      </div>
                    </div>
                    <div className="review-rating">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  
                  <div className="review-content">
                    <h3 className="review-title">{review.title}</h3>
                    <p className="review-text">{review.content}</p>
                  </div>
                  
                  <div className="review-footer">
                    <button 
                      className={`helpful-btn ${votedReviews.has(review.id) ? 'voted' : ''}`}
                      onClick={() => handleHelpfulClick(review.id, review.helpful || 0)}
                      disabled={votedReviews.has(review.id)}
                    >
                      <ThumbsUp className="helpful-icon" />
                      {votedReviews.has(review.id) ? 'Thank you!' : `Helpful (${review.helpful || 0})`}
                    </button>
                  </div>

{/* Admin Replies Section - Simplified */}
{replies[review.id] && replies[review.id].length > 0 && (
  <div className="admin-response">
    <div className="admin-response-content">
      <strong>Admin Response:</strong> {replies[review.id][0].reply_content}
    </div>
  </div>
)}
</div>
))
) : (
  <div className="no-reviews">
    <h3>No reviews found</h3>
    <p>Be the first to share your experience!</p>
  </div>
)}
</div>
</div>
</section>

{/* Write Review CTA */}
<section className="section bg-light">
  <div className="container">
    <div className="write-review-cta text-center">
      <h2>Share Your Experience</h2>
      <p className="mb-6">
        Have you traded on Kenny Miles Exchange? We'd love to hear about your experience!
      </p>
      <button 
        className="btn btn-primary"
        onClick={() => setShowReviewForm(true)}
      >
        Write a Review
      </button>
    </div>
  </div>
</section>

{/* Review Submission Form Modal */}
{showReviewForm && (
  <div className="review-modal-overlay">
    <div className="review-modal">
      <div className="review-modal-header">
        <h3>Write a Review</h3>
        <button 
          className="close-btn"
          onClick={() => setShowReviewForm(false)}
        >
          <X size={24} />
        </button>
      </div>
      
      <form onSubmit={handleReviewSubmit} className="review-form">
        <div className="form-group">
          <label htmlFor="user_name">Your Name *</label>
          <input
            type="text"
            id="user_name"
            name="user_name"
            value={reviewForm.user_name}
            onChange={handleInputChange}
            required
            placeholder="Enter your name"
          />
        </div>

        <div className="form-group">
          <label>Rating *</label>
          <div className="rating-input">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`star-btn ${star <= reviewForm.rating ? 'active' : ''}`}
                onClick={() => handleRatingClick(star)}
              >
                <StarFilled size={24} />
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="title">Review Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={reviewForm.title}
            onChange={handleInputChange}
            required
            placeholder="Summarize your experience"
          />
        </div>

        <div className="form-group">
          <label htmlFor="content">Your Review *</label>
          <textarea
            id="content"
            name="content"
            value={reviewForm.content}
            onChange={handleInputChange}
            required
            rows={5}
            placeholder="Tell us about your trading experience..."
          />
        </div>

        {submitStatus === 'success' && (
          <div className="submit-status success">
            ✓ Review submitted successfully!
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="submit-status error">
            ✗ Please fill in all required fields.
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowReviewForm(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
            {!submitting && <Send size={16} />}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
</div>
);
};

export default Reviews;